import { useState } from 'react';
import {
  MAFIA_COUNTS,
  MIN_PLAYERS,
  SETTING_LIMITS,
  SPECIAL_ROLES,
  SPECIAL_ROLE_SETTING_KEYS,
  type MafiaCount,
  type SettingsPatch,
  type SpecialRole,
} from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { unlockAudio } from '@/lib/sound';
import { markSoundOffered, wasSoundOffered } from '@/lib/soundPrompt';
import { AppShell } from '@/components/AppShell';
import { GameHeader } from '@/components/GameHeader';
import { PlayerCard } from '@/components/PlayerCard';
import { Button } from '@/components/Button';
import { Stepper } from '@/components/Stepper';
import { Toggle } from '@/components/Toggle';

export function LobbyScreen() {
  const view = useAppStore((s) => s.view);
  // Wird nicht zurückgesetzt: sobald der Server startet, wechselt die Phase und
  // dieser Screen verschwindet ohnehin.
  const [starting, setStarting] = useState(false);
  if (!view) return null;

  const isHost = view.self.isHost;
  const settings = view.settings;
  const enabledSpecials = SPECIAL_ROLES.filter(
    (role) => settings.specialRoles[SPECIAL_ROLE_SETTING_KEYS[role]],
  ).length;

  const updateSettings = (patch: SettingsPatch) => {
    wsClient.send({ type: 'settings:update', patch });
  };

  return (
    <AppShell
      header={<GameHeader view={view} />}
      footer={
        isHost ? (
          <div className="flex flex-col gap-2">
            {!view.canStart && view.startIssue && (
              <p className="text-center text-sm text-fg-muted">
                {de.lobby.startIssue(view.startIssue, MIN_PLAYERS, view.players.length)}
              </p>
            )}
            <Button
              fullWidth
              disabled={!view.canStart}
              pending={starting}
              onClick={() => {
                setStarting(true);
                wsClient.send({ type: 'game:start' });
              }}
            >
              {de.lobby.start}
            </Button>
          </div>
        ) : (
          <p className="py-2 text-center text-sm text-fg-muted">
            {view.canStart
              ? de.lobby.waitingForHost
              : view.startIssue && de.lobby.startIssue(view.startIssue, MIN_PLAYERS, view.players.length)}
          </p>
        )
      }
    >
      <div className="mx-auto flex max-w-xl flex-col gap-6 pb-4 sm:max-w-2xl lg:grid lg:max-w-4xl lg:grid-cols-2 lg:items-start lg:gap-8">
        <SoundOffer />

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">{view.roomName}</h2>
            <span className="text-sm text-fg-muted">{de.lobby.players(view.players.length)}</span>
          </div>
          <ul className="flex flex-col gap-2">
            {view.players.map((player) => (
              <li key={player.id}>
                <PlayerCard
                  player={player}
                  isSelf={player.id === view.self.id}
                  trailing={
                    isHost && player.id !== view.self.id ? (
                      <button
                        type="button"
                        onClick={() => wsClient.send({ type: 'room:kick', playerId: player.id })}
                        aria-label={`${player.name} entfernen`}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-fg-faint hover:bg-surface-2 hover:text-fg"
                      >
                        ✕
                      </button>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="mb-2 font-display text-lg font-semibold">{de.lobby.settings}</h2>

          <RoleComposition
            playerCount={view.players.length}
            mafiaCount={settings.mafiaCount}
            specials={SPECIAL_ROLES.filter(
              (role) => settings.specialRoles[SPECIAL_ROLE_SETTING_KEYS[role]],
            )}
          />

          <div className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface px-4">
            <div className="py-2.5">
              <span className="mb-2 block text-sm text-fg-muted">{de.lobby.mafiaCount}</span>
              <div className="flex gap-2">
                {MAFIA_COUNTS.map((count) => {
                  const allowed = view.allowedMafiaCounts.includes(count);
                  const active = settings.mafiaCount === count;
                  return (
                    <button
                      key={count}
                      type="button"
                      disabled={!isHost || !allowed}
                      onClick={() => updateSettings({ mafiaCount: count as MafiaCount })}
                      className={`min-h-11 flex-1 rounded-xl font-display font-semibold transition-colors disabled:opacity-30 ${
                        active ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-fg'
                      }`}
                    >
                      {count}
                    </button>
                  );
                })}
              </div>
            </div>

            <Stepper
              label={de.lobby.startCountdown}
              value={settings.startCountdownSeconds}
              min={SETTING_LIMITS.startCountdownSeconds.min}
              max={SETTING_LIMITS.startCountdownSeconds.max}
              step={1}
              suffix="s"
              disabled={!isHost}
              onChange={(startCountdownSeconds) => updateSettings({ startCountdownSeconds })}
            />
            <Stepper
              label={de.lobby.discussion}
              value={settings.discussionSeconds}
              min={SETTING_LIMITS.discussionSeconds.min}
              max={SETTING_LIMITS.discussionSeconds.max}
              step={15}
              suffix="s"
              disabled={!isHost}
              onChange={(discussionSeconds) => updateSettings({ discussionSeconds })}
            />
            <Stepper
              label={de.lobby.voting}
              value={settings.votingSeconds}
              min={SETTING_LIMITS.votingSeconds.min}
              max={SETTING_LIMITS.votingSeconds.max}
              step={5}
              suffix="s"
              disabled={!isHost}
              onChange={(votingSeconds) => updateSettings({ votingSeconds })}
            />
            <Stepper
              label={de.lobby.tieBreaker}
              value={settings.tieBreakerSeconds}
              min={SETTING_LIMITS.tieBreakerSeconds.min}
              max={SETTING_LIMITS.tieBreakerSeconds.max}
              step={5}
              suffix="s"
              disabled={!isHost}
              onChange={(tieBreakerSeconds) => updateSettings({ tieBreakerSeconds })}
            />
            <Stepper
              label={de.lobby.maxTieRounds}
              value={settings.maxTieRounds}
              min={SETTING_LIMITS.maxTieRounds.min}
              max={SETTING_LIMITS.maxTieRounds.max}
              step={1}
              disabled={!isHost}
              onChange={(maxTieRounds) => updateSettings({ maxTieRounds })}
            />
            <Toggle
              label={de.lobby.allowAbstain}
              checked={settings.allowAbstain}
              disabled={!isHost}
              onChange={(allowAbstain) => updateSettings({ allowAbstain })}
            />
            <Toggle
              label={de.lobby.lockRoom}
              checked={view.locked}
              disabled={!isHost}
              onChange={(locked) => wsClient.send({ type: 'room:setLocked', locked })}
            />
          </div>

          <h2 className="mt-6 mb-1 font-display text-lg font-semibold">{de.lobby.specialRoles}</h2>
          <p className="mb-2 text-sm text-fg-muted">
            {de.lobby.specialRolesHint(view.maxSpecialRoles)}
          </p>
          <div className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface px-4">
            {SPECIAL_ROLES.map((role) => {
              const key = SPECIAL_ROLE_SETTING_KEYS[role];
              const enabled = settings.specialRoles[key];
              return (
                <Toggle
                  key={role}
                  label={`${de.specialRoles[role].emoji} ${de.roles[role]}`}
                  description={de.specialRoles[role].lobbyHint}
                  checked={enabled}
                  // Spiegelt die Server-Prüfung: die UI soll nie eine Kombination
                  // anbieten, die anschließend abgelehnt wird.
                  disabled={!isHost || (!enabled && enabledSpecials >= view.maxSpecialRoles)}
                  onChange={(next) => updateSettings({ specialRoles: { [key]: next } })}
                />
              );
            })}
          </div>

          <Button
            variant="ghost"
            fullWidth
            className="mt-4"
            onClick={() => wsClient.send({ type: 'room:leave' })}
          >
            {de.lobby.leave}
          </Button>
        </section>
      </div>
    </AppShell>
  );
}

/**
 * Einmaliges Ton-Angebot.
 *
 * Bewusst ein Kärtchen mit echtem Schalter und kein Toast: Ein Hinweis, der
 * nur „schalt doch den Ton ein" sagt, schickt den Spieler wieder ins Menü
 * suchen. Kein Modal — die Lobby bleibt bedienbar, und Wegtippen ist genauso
 * gültig wie Zustimmen.
 */
function SoundOffer() {
  const soundEnabled = useAppStore((s) => s.soundEnabled);
  const setSoundEnabled = useAppStore((s) => s.setSoundEnabled);
  const [dismissed, setDismissed] = useState(wasSoundOffered);

  if (dismissed || soundEnabled) return null;

  const close = () => {
    markSoundOffered();
    setDismissed(true);
  };

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-surface-border bg-surface px-4 py-3 lg:col-span-2">
      <span aria-hidden="true">🔊</span>
      <p className="min-w-0 flex-1 text-sm text-fg-muted">{de.lobby.soundOffer}</p>
      <Button
        variant="secondary"
        className="min-h-11 shrink-0 px-4 text-sm"
        onClick={() => {
          setSoundEnabled(true);
          // Der Tap ist die Nutzergeste, auf die der AudioContext wartet —
          // ohne das Entsperren hier bliebe der erste Ton stumm.
          unlockAudio();
          close();
        }}
      >
        {de.lobby.soundOfferCta}
      </Button>
      <button
        type="button"
        onClick={close}
        aria-label={de.lobby.soundOfferDismiss}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-fg-faint hover:bg-surface-2 hover:text-fg"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Was aus den Einstellungen tatsächlich wird.
 *
 * Der Host schiebt Mafia-Anzahl und Sonderrollen hin und her, ohne zu sehen,
 * wie viele einfache Bürger am Ende übrig bleiben — und genau diese Zahl trägt
 * die Entscheidung. Rein clientseitig gerechnet: die Grenzen selbst kommen
 * weiterhin vom Server (`allowedMafiaCounts`, `maxSpecialRoles`), hier steht
 * nur die Summe, die sich aus ihnen ergibt.
 */
function RoleComposition({
  playerCount,
  mafiaCount,
  specials,
}: {
  playerCount: number;
  mafiaCount: number;
  specials: readonly SpecialRole[];
}) {
  const plainCitizens = playerCount - mafiaCount - specials.length;
  // Solange noch niemand da ist, rechnet die Zeile „0 Bürger" aus und stiftet
  // damit mehr Verwirrung als Nutzen — der Starthinweis sagt ohnehin, was fehlt.
  if (plainCitizens < 1) return null;

  return (
    <p className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-medium text-mafia">
        ☠️ {de.lobby.compositionMafia(mafiaCount)}
      </span>
      {specials.map((role) => (
        <span key={role} className="text-fg-muted">
          · {de.specialRoles[role].emoji} {de.roles[role]}
        </span>
      ))}
      <span className="font-medium text-citizen">
        · 🏘️ {de.lobby.compositionCitizens(plainCitizens)}
      </span>
    </p>
  );
}
